import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { API_URLS } from '../../constants/ApiUrls';
import { eosGet } from '../../services/EosApi';
import { getAxiosErrorMessage } from '../../utils/general';
import {
  ReportTypesResponseItem,
  ReportTypesState,
  ReportTypeOption,
  ReportTypesLookup,
} from '../types/reportTypes.types';

const initialState: ReportTypesState = {
  loading: false,
  error: null,
  data: [],
  lookup: { forecast: {}, scenario: {} },
  lastFetchedPortfolioId: null,
};

const buildLookup = (data: ReportTypesResponseItem[]): ReportTypesLookup => {
  const forecast: Record<number, ReportTypeOption[]> = {};
  const scenario: Record<number, ReportTypeOption[]> = {};

  data.forEach((item) => {
    item.forecastContractsByCountry?.forEach((country) => {
      country.contracts?.forEach((contract) => {
        forecast[contract.supplyContractId] = contract.reportTypes || [];
      });
    });

    item.scenarioContractsByCountry?.forEach((country) => {
      country.contracts?.forEach((contract) => {
        scenario[contract.supplyContractId] = contract.reportTypes || [];
      });
    });
  });

  return { forecast, scenario };
};

export const fetchReportTypesForSupplyContract = createAsyncThunk<
  { data: ReportTypesResponseItem[]; portfolioId: number },
  number,
  { rejectValue: string }
>(
  'reportTypes/fetchReportTypesForSupplyContract',
  async (portfolioId, thunkApi) => {
    const { rejectWithValue } = thunkApi;

    if (!portfolioId) {
      return rejectWithValue('Portfolio id is required');
    }

    try {
      const { data } = await eosGet(
        `${API_URLS.EOS_REPORT_TYPES_FOR_SUPPLY_CONTRACT}?portfolioId=${portfolioId}`
      );

      let normalizedData: ReportTypesResponseItem[];
      if (Array.isArray(data)) {
        normalizedData = data;
      } else if (data) {
        normalizedData = [data];
      } else {
        normalizedData = [];
      }

      return { data: normalizedData, portfolioId };
    } catch (error: unknown) {
      const message = getAxiosErrorMessage(
        error,
        'Failed to fetch report types for supply contract'
      );
      return rejectWithValue(message);
    }
  }
);

const reportTypesSlice = createSlice({
  name: 'reportTypes',
  initialState,
  reducers: {
    resetReportTypesState: (state) => {
      state.data = [];
      state.error = null;
      state.loading = false;
      state.lastFetchedPortfolioId = null;
      state.lookup = { forecast: {}, scenario: {} };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReportTypesForSupplyContract.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportTypesForSupplyContract.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.data = action.payload.data;
        state.lookup = buildLookup(action.payload.data);
        state.lastFetchedPortfolioId = action.payload.portfolioId;
      })
      .addCase(fetchReportTypesForSupplyContract.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ?? action.error.message ?? null;
      });
  },
});

export const { resetReportTypesState } = reportTypesSlice.actions;

export const getReportTypesState = (state: { reportTypes: ReportTypesState }) =>
  state.reportTypes;

export const getReportTypesData = (state: { reportTypes: ReportTypesState }) =>
  state.reportTypes.data;

export const getForecastReportTypesByContractId = (state: {
  reportTypes: ReportTypesState;
}) => state.reportTypes.lookup.forecast;

export const getScenarioReportTypesByContractId = (state: {
  reportTypes: ReportTypesState;
}) => state.reportTypes.lookup.scenario;

export default reportTypesSlice.reducer;
